import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private http = inject(HttpClient);
  private readonly BASE = `${environment.apiUrl}/support/chats`;

  getChats(params: {
    status?:   string;
    userRole?: string;
    search?:   string;
    archived?: boolean;
    page?:     number;
    limit?:    number;
  }): Observable<any> {
    const p: Record<string, any> = { page: params.page ?? 1, limit: params.limit ?? 20 };
    if (params.status)   p['status']   = params.status;
    if (params.userRole) p['userRole'] = params.userRole;
    if (params.search)   p['search']   = params.search;
    if (params.archived) p['archived'] = true;
    return this.http.get<any>(this.BASE, { params: p });
  }

  // PUT /support/chats/:id/archive — { archive: true } archives, false unarchives.
  archiveChat(chatId: string, archive: boolean): Observable<any> {
    return this.http.put<any>(`${this.BASE}/${chatId}/archive`, { archive });
  }

  getChatById(chatId: string, page = 1, limit = 50): Observable<any> {
    return this.http.get<any>(`${this.BASE}/${chatId}`, { params: { page, limit } });
  }

  sendMessage(chatId: string, body: { type: 'text' | 'image' | 'file'; text?: string; attachment?: { url: string; name: string; mimeType: string; sizeBytes: number } }): Observable<any> {
    return this.http.post<any>(`${this.BASE}/${chatId}/messages`, body);
  }

  updateStatus(chatId: string, status: 'open' | 'pending' | 'resolved'): Observable<any> {
    return this.http.put<any>(`${this.BASE}/${chatId}/status`, { status });
  }

  // PUT /support/chats/:chatId/messages/:messageId — edit an admin text message.
  editMessage(chatId: string, messageId: string, text: string): Observable<any> {
    return this.http.put<any>(`${this.BASE}/${chatId}/messages/${messageId}`, { text });
  }

  uploadAttachment(file: File): Observable<{ url: string; name: string; mimeType: string; sizeBytes: number }> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<any>(`${environment.apiUrl}/support/messages/upload`, form).pipe(
      map((res: any) => res?.data?.attachment ?? res?.data ?? res)
    );
  }

  getUnreadSummary(): Observable<{ totalChats: number; totalMessages: number }> {
    return this.http.get<any>(`${environment.apiUrl}/support/unread-summary`).pipe(
      map((res: any) => ({
        totalChats:    res?.data?.totalChats    ?? 0,
        totalMessages: res?.data?.totalMessages ?? 0
      }))
    );
  }
}
